import { useState } from 'react';
import DecideApplicationModal from './DecideApplicationModal';
import { useSendMessage } from '../../api/hooks/useSendMessage';
import SendApplicationModal from './SendApplicationModal';
import { ContentTab } from '../../pages/Schreibtisch/SchreibtischPage';

import { useSubmissionsAsNachrichten } from '../../api/hooks/useSubmissionsAsNachrichten';
import { Bescheidstatus } from '../../api/models/Status/Bescheidstatus';
import {
  UploadFilesConfig,
  useUploadFiles
} from '../../shared/hooks/useUploadFiles';
import { Modal, Button } from '@efast_public/fjd-component-library';
import { NachrichtType } from '../../api/models/Nachrichten/NachrichtModel';
import { useQuery } from '@tanstack/react-query';
import { CaseDetailsModel } from '../../api/models/Case/CaseDetailsModel';
import { BehApiRoutes } from '../../api/beh-api';
import { KommunikationRequestModel } from '../NachrichtSenden/KommunikationRequestModel';
import { useProfile } from '../../shared/hooks/AuthContext';

interface SwitchApplicationModalsProps {
  readonly onClose: () => void;
  readonly organisationUnitName: string;
  readonly caseId: string;
  readonly setContentTabState: (val: ContentTab) => void;
  readonly openAbschliessenModal: () => void;
}

// TODO: We need to simplify this modal component, pretty complex right now
export default function SwitchApplicationModals({
  caseId,
  ...props
}: SwitchApplicationModalsProps) {
  const { data: caseDetails, refetch: refreshCaseDetails } = useQuery<
    CaseDetailsModel,
    Error
  >({
    queryKey: [BehApiRoutes.case(caseId ?? '')]
  });
  const { profile } = useProfile();
  const version = caseDetails?.version ?? '';
  const [openSendenModal, setOpenSendenModal] = useState<boolean>(false);
  const [applicationIsPositiveDecided, setApplicationIsPositiveDecided] =
    useState<boolean | undefined>(undefined);
  const { sendMessage, loadingSend } = useSendMessage();
  const { refetch: mutate, error: useSubmissionsAsNachrichtenError } =
    useSubmissionsAsNachrichten({
      caseId
    });
  const [subject, setSubject] = useState<string>();
  const [text, setText] = useState<string>();

  const { uploadState, dispatch, confirmAction } = useUploadFiles({
    withConfirmation: true,
    deleteFilesWhenRemoveBescheid: true
  } as UploadFilesConfig);

  function openSendApplicationModal(
    applicationIsPositiveDecidedVar: boolean
  ): void {
    setApplicationIsPositiveDecided(applicationIsPositiveDecidedVar);
    setOpenSendenModal(true);
  }

  function returnToDecideApplicationModal(subject: string, text: string): void {
    setSubject(subject);
    setText(text);
    setOpenSendenModal(false);
  }

  async function performSentApplicationActions(
    subject: string,
    text: string
  ): Promise<void> {
    const payload: KommunikationRequestModel = {
      absender: `${profile?.firstName ?? ''} ${profile?.lastName ?? ''}`,
      betreff: subject,
      nachrichttext: text,
      attachments: uploadState.bescheidUpload
        ? [uploadState.bescheidUpload, ...uploadState.additionalFiles]
        : uploadState.additionalFiles,
      nachrichtType: NachrichtType.BESCHEID_DIGITAL,
      bescheidstatus: applicationIsPositiveDecided
        ? Bescheidstatus.POSITIV
        : Bescheidstatus.NEGATIV,
      caseVersion: version
    };
    await sendMessage({ payload, caseId: caseId })
      .then(() => mutate())
      .catch(() => {})
      .finally(() => refreshCaseDetails());

    props.onClose();
    props.setContentTabState(ContentTab.KOMMUNIKATION);
  }

  return (
    <div>
      <Modal
        appElement={document.querySelector('.fjd-base-layout') as HTMLElement}
        closeOnBackdropClick
        open={!!useSubmissionsAsNachrichtenError}
        onClose={() => props.onClose()}
        data-testid={
          openSendenModal ? 'send-application-modal' : 'attachment-modal'
        }
      >
        <Modal.Header
          id="SwitchApplicationModals-header"
          onClose={props.onClose}
        >
          {openSendenModal
            ? confirmAction.toConfirm
              ? confirmAction.heading
              : 'Bescheid versenden'
            : confirmAction.toConfirm
              ? confirmAction.heading
              : 'Antrag bescheiden'}
        </Modal.Header>
        <Modal.Content />
        <Modal.Footer>
          <Button variant="primary" onClick={() => props.onClose()}>
            Schließen
          </Button>
        </Modal.Footer>
      </Modal>
      {!openSendenModal && !useSubmissionsAsNachrichtenError && (
        <DecideApplicationModal
          caseId={caseId}
          closeOnSecondaryButtonClick={() => {
            props.onClose();
          }}
          onContinueButtonClick={openSendApplicationModal}
          isPositiveDecided={applicationIsPositiveDecided}
          openAbschliessenModal={() => props.openAbschliessenModal()}
          fileDispatch={dispatch}
          isLoadingSend={loadingSend}
          filesUploadstate={uploadState}
          confirmAction={confirmAction}
          isOpen
        />
      )}
      {openSendenModal && !useSubmissionsAsNachrichtenError && (
        <SendApplicationModal
          caseId={caseId}
          closeOnSecondaryButtonClick={() => {
            props.onClose();
          }}
          returnOnSecondaryButtonClick={returnToDecideApplicationModal}
          performApplicationModalActions={performSentApplicationActions}
          isApplicationPositiveDecided={applicationIsPositiveDecided!}
          setApplicationIsPositiveDecided={setApplicationIsPositiveDecided}
          subject={subject}
          text={text}
          isLoadingSend={loadingSend}
          filesUploadstate={uploadState}
          fileDispatch={dispatch}
          confirmAction={confirmAction}
          isOpen
        />
      )}
    </div>
  );
}
